// Content service for fetching all user-editable content

export interface Project {
  id: string;
  title: string;
  description: string;
  year: number;
  status: 'completed' | 'ongoing' | 'planned';
  links: {
    github?: string;
    youtube?: string;
    demo?: string;
    documentation?: string;
    paper?: string;
    website?: string;
    [key: string]: string | undefined;
  };
  technologies: string[];
  image?: string;
}

export interface PersonalInfo {
  name: string;
  title: string;
  affiliation: string;
  email: string;
  location: string;
  bio: string;
  interests: string[];
  social: {
    github?: string;
    linkedin?: string;
    twitter?: string;
    scholar?: string;
    orcid?: string;
    [key: string]: string | undefined;
  };
  cv?: string;
  profileImage?: string;
}

// Cache for content
const contentCache = new Map<string, any>();

// Generic fetch function with caching
const fetchContent = async <T>(url: string, cacheKey: string): Promise<T> => {
  // Add cache-busting parameter for development
  const cacheBuster = `?t=${Date.now()}`;
  const urlWithCacheBuster = url + cacheBuster;
  
  // Disable cache for now to ensure fresh data
  // if (contentCache.has(cacheKey)) {
  //   return contentCache.get(cacheKey);
  // }

  try {
    const response = await fetch(urlWithCacheBuster);
    if (!response.ok) {
      throw new Error(`Failed to fetch ${cacheKey}: ${response.statusText}`);
    }
    
    const data = await response.json();
    contentCache.set(cacheKey, data);
    return data;
  } catch (error) {
    console.error(`Error fetching ${cacheKey}:`, error);
    throw error;
  }
};

// Fetch personal information
export const fetchPersonalInfo = async (): Promise<PersonalInfo> => {
  return fetchContent<PersonalInfo>('/content/personal-info.json', 'personalInfo');
};

// Fetch projects
export const fetchProjects = async (): Promise<Project[]> => {
  const data = await fetchContent<{ projects: Project[] }>('/content/projects.json', 'projects');
  return data.projects;
};

// Clear cache (useful for development)
export const clearContentCache = (): void => {
  contentCache.clear();
};
